import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiSparkles } from '@lumapps/lumx/icons';
import { Button, Emphasis, Theme } from '@lumapps/lumx/react';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useTranslate } from '@lumapps/translations';

import { CLASSNAME } from '../../constants';
import { WIDGET_SUMMARY } from '../../keys';

export interface ButtonGenerateProps {
    /** Forwarded class name */
    className?: string;
    /** Callback called on generate button click */
    onGenerate: (prompt: string) => Promise<void>;
    /** Whether a text has already been generated before */
    hasTextBeenGenerated: boolean;
    /** Whether a generation is ongoing */
    isGeneratingSummary: boolean;
    /** Collect the text to summarize from the surface the button is rendered on */
    getTextToSummarize: () => string;
    /** Gates the button only, so a title without text can still be summarized. The caller sends the title itself */
    contentTitle?: string;
    theme?: Theme;
}

/**
 * Button for generate summary with AI
 * @param ButtonGenerateProps
 * @returns ButtonGenerate
 */
export const ButtonGenerate: React.FC<ButtonGenerateProps> = ({
    className,
    onGenerate,
    hasTextBeenGenerated,
    isGeneratingSummary,
    getTextToSummarize,
    contentTitle,
    theme = Theme.light,
}) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(CLASSNAME);

    const { info: infoNotification } = useNotification();

    const handleGenerate = () => {
        const textualWidgetsContent = getTextToSummarize();

        if (isEmpty(textualWidgetsContent) && isEmpty(contentTitle?.trim())) {
            infoNotification({ content: translateKey(WIDGET_SUMMARY.NO_TEXTUAL_WIDGET_INFO) });
        } else {
            onGenerate(textualWidgetsContent);
        }
    };

    return (
        <Button
            className={className}
            leftIcon={mdiSparkles}
            emphasis={Emphasis.low}
            onClick={handleGenerate}
            isDisabled={isGeneratingSummary}
            theme={theme}
            {...get({ element: 'button', action: 'generate-ai-summary' })}
        >
            {hasTextBeenGenerated
                ? translateKey(WIDGET_SUMMARY.REGENERATE_SUMMARY)
                : translateKey(WIDGET_SUMMARY.GENERATE_SUMMARY)}
        </Button>
    );
};
